import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';

const PaymentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  useEffect(() => {
    fetchPayment();
  }, [id]);

  const fetchPayment = async () => {
    try {
      const response = await api.get(`/payments/${id}`);
      setPayment(response.data.data);
    } catch (error) {
      toast.error('Failed to load payment');
      navigate('/payments');
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async () => {
    if (!refundAmount || !refundReason) {
      toast.error('Please fill in all fields');
      return;
    }

    setUpdating(true);
    try {
      await api.post(`/payments/${id}/refund`, {
        refundAmount: parseFloat(refundAmount),
        reason: refundReason
      });
      toast.success('Refund processed successfully!');
      setShowRefundModal(false);
      setRefundAmount('');
      setRefundReason('');
      fetchPayment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process refund');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Payment not found</p>
        <Link to="/payments" className="text-primary-600 hover:text-primary-700">
          Back to payments
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/payments')}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
          >
            ←
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Payment Details</h1>
        </div>
        <div className="flex w-full sm:w-auto">
          {user?.role === 'admin' && payment.status === 'completed' && !payment.refundId && (
            <button
              onClick={() => setShowRefundModal(true)}
              className="w-full sm:w-auto px-4 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 font-medium transition-colors"
            >
              Process Refund
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Amount</p>
                <p className="text-2xl font-bold text-gray-900">₹{payment.amount?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Currency</p>
                <p className="font-medium text-gray-900">{payment.currency}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Payment Method</p>
                <p className="font-medium capitalize text-gray-900">{payment.paymentMethod || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                  {payment.status}
                </span>
              </div>
            </div>
          </div>

          {payment.razorpayOrderId && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Razorpay Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="overflow-hidden">
                  <p className="text-sm text-gray-600">Order ID</p>
                  <p className="font-medium font-mono text-sm text-gray-900 break-all">{payment.razorpayOrderId}</p>
                </div>
                {payment.razorpayPaymentId && (
                  <div className="overflow-hidden">
                    <p className="text-sm text-gray-600">Payment ID</p>
                    <p className="font-medium font-mono text-sm text-gray-900 break-all">{payment.razorpayPaymentId}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {payment.refundId && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Refund Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Refund ID</p>
                  <p className="font-medium font-mono text-sm">{payment.refundId}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Refund Amount</p>
                  <p className="font-medium">₹{payment.refundAmount?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Refund Status</p>
                  <p className="font-medium capitalize">{payment.refundStatus}</p>
                </div>
                {payment.refundedAt && (
                  <div>
                    <p className="text-sm text-gray-600">Refunded At</p>
                    <p className="font-medium">{new Date(payment.refundedAt).toLocaleString()}</p>
                  </div>
                )}
              </div>
              {payment.refundReason && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600">Refund Reason</p>
                  <p className="font-medium">{payment.refundReason}</p>
                </div>
              )}
            </div>
          )}

          {payment.description && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
              <p className="text-gray-700">{payment.description}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Patient</h2>
            <p className="font-medium text-gray-900">{payment.lead?.patientName}</p>
            <p className="text-sm text-gray-600">{payment.lead?.patientPhone}</p>
            {payment.lead?.disease && (
              <p className="text-sm text-gray-600 mt-2 italic">{payment.lead.disease}</p>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payer</h2>
            <p className="font-medium">
              {payment.paidBy?.firstName} {payment.paidBy?.lastName}
            </p>
            <p className="text-sm text-gray-600 capitalize">{payment.paidByRole}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Verification</h2>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Verified</p>
                <p className="font-medium">{payment.isVerified ? 'Yes' : 'No'}</p>
              </div>
              {payment.verifiedAt && (
                <div>
                  <p className="text-sm text-gray-600">Verified At</p>
                  <p className="font-medium">{new Date(payment.verifiedAt).toLocaleString()}</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h2>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Created</p>
                <p className="font-medium">{new Date(payment.createdAt).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Last Updated</p>
                <p className="font-medium">{new Date(payment.updatedAt).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Refund Modal */}
      {showRefundModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Process Refund</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Refund Amount (Max: ₹{payment.amount?.toLocaleString()})
                </label>
                <input
                  type="number"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  max={payment.amount}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowRefundModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRefund}
                disabled={updating}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {updating ? 'Processing...' : 'Process Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentDetail;
